import { DATA_ROW_MIN_HEIGHT } from "./constants";

/**
 * Vrátí vnější výšku elementu, tzn. výška doplněná o marginTop a marginBottom.
 * @param element Element, pro který se výška vypočítá.
 */
export const getOuterHeight = (element: HTMLElement) => {
    const style = getComputedStyle(element);
    return element.getBoundingClientRect().height + parseInt(style.marginTop) + parseInt(style.marginBottom);
};

/**
 * Vrátí vnější výšku prvního potomka elementu (výška doplněná o marginTop a marginBottom).
 * @param element Element, pro který se výška vypočítá.
 */
export const getFirstChildOuterHeight = (element: Element) => (
    element.firstChild ? getOuterHeight(element.firstChild as HTMLElement) : 0
);

/**
 * Vrátí výšky potomků.
 * @param element Element, pro který vrací výšky potomků.
 * @param getHeight Metoda pro získání výšky.
 */
export const getChildrenHeights = (element: Element, getHeight: (element: Element) => number) => {
    const heights = [];
    for (let i = 0; i < element.childElementCount; i++) {
        const child = element.children.item(i) as HTMLElement;
        heights.push(getHeight(child));
    }
    return heights;
};

/**
 * Vypočítá na základě výšky elementu a minimální výšky řádku, kolik řádků se přibližně vejde do tohoto elementu.
 * Pokud je element tak nízký, že se do něj nevejde žádný řádek, bude vrácen počet řádků 1,
 * jelikož v takovém případě potřebuje mít připraven alespoň 1 načtený element pro zobrazení při případném zvětšení elementu.
 * Používá se v komponentách, které zobrazují seznamy (např.: ResponsiveList).
 * @param element Element, pro který se výpočet provádí.
 * @param rowMinHeight Minimální výška řádku, vychozí hodnotou je konstanta *DATA_ROW_MIN_HEIGHT*.
 */
export const getApproximateRowsCount = (height: number, rowMinHeight: number = DATA_ROW_MIN_HEIGHT) => {
    const rowsCount = Math.floor(height / rowMinHeight);
    return rowsCount !== 0 ? rowsCount : 1;
};

/*
* Výčet sousedních elementů.
*/
export enum DataRowElementSiblings {
    nextElementSibling = "nextElementSibling",
    previousElementSibling = "previousElementSibling",
 }

/**
 * Typ funkce, která vrací sousední element podle siblingType.
 *
 * @param siblingType Určuje, zda hledáme předchozí nebo následující element.
 * @param element Element, pro který hledám souseda.
 *
 */
export type GetDataRowSiblingType = (siblingType: DataRowElementSiblings, element: Element | null) => HTMLElement | undefined;

/**
 * Metoda pro získání sousedního řádku. Závisí na struktuře DOMu.
 *
 * @param siblingType Určení kterého sourozence chceme (předchozím/následující).
 * @param element Element kterému chceme určit sourozence.
 */
export const getDataRowSibling: GetDataRowSiblingType = (siblingType, element): HTMLElement | undefined => {
    const sibling = element?.parentElement?.[siblingType]?.firstChild;
    if (sibling) {
        return sibling as HTMLElement;
    }
    return undefined;
};
